import { useCallback, useEffect, useRef, useState } from 'react';

/** Every vite build names its entry chunk after the content hash, so the name IS the build id. */
const ENTRY_CHUNK = /assets\/index-[A-Za-z0-9_-]+\.js/;

const POLL_INTERVAL_MS = 15 * 60 * 1000;

/**
 * A tab coming back into focus is the moment worth checking - that is when someone
 * returns to a board they left open this morning. Flicking between windows is not,
 * so a check is not repeated more often than this.
 */
const FOCUS_THROTTLE_MS = 60 * 1000;

/**
 * The entry chunk this tab is running. import.meta.url is the module's own URL in a
 * build; the dev server and any build that inlines the entry fall through to the
 * script tag, and to null when there is nothing to compare (dev), which disables
 * the check rather than guessing.
 */
const runningBuild = (): string | null => {
  const fromModule = ENTRY_CHUNK.exec(import.meta.url);
  if (fromModule) return fromModule[0];

  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  const fromTag = script ? ENTRY_CHUNK.exec(script.src) : null;
  return fromTag ? fromTag[0] : null;
};

/**
 * The entry chunk the server is handing out now. no-store and not no-cache: this
 * request exists to learn the truth, so it must not be answered from the cache the
 * page was loaded from.
 */
const deployedBuild = async (): Promise<string | null> => {
  const response = await fetch(`${import.meta.env.BASE_URL}index.html`, { cache: 'no-store' });
  if (!response.ok) return null;

  const found = ENTRY_CHUNK.exec(await response.text());
  return found ? found[0] : null;
};

/**
 * True once the server is serving a build other than the one this tab loaded.
 *
 * Never flips back: a reload is the only way out, and a banner that came and went
 * on its own would be worse than none. Deliberately does not reload by itself -
 * a coordinator halfway through a form would lose the form.
 */
const useNewBuildAvailable = (): boolean => {
  const [isStale, setIsStale] = useState(false);
  const running = useRef<string | null>(runningBuild());
  const lastCheckedAt = useRef(0);

  const check = useCallback(async () => {
    if (!running.current) return;

    lastCheckedAt.current = Date.now();
    try {
      const deployed = await deployedBuild();
      if (deployed && deployed !== running.current) setIsStale(true);
    } catch {
      // Offline, or a filtered ISP blocking the request. Nothing to report and
      // nothing to do - the next check asks again.
    }
  }, []);

  useEffect(() => {
    // No check on mount: this tab just loaded whatever the server has.
    if (isStale) return;

    const poll = window.setInterval(check, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      const isBack = document.visibilityState === 'visible';
      if (isBack && Date.now() - lastCheckedAt.current > FOCUS_THROTTLE_MS) check();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [check, isStale]);

  return isStale;
};

export default useNewBuildAvailable;
